import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, SectionTitle, StatusDot, LearnLink } from "@/components/ui-kit";
import {
  Cpu, Bot, CircuitBoard, Settings as SettingsIcon, Camera, Brain,
  Network, Radio, Globe, ServerCog,
} from "lucide-react";

const ICONS = {
  cpu: Cpu, bot: Bot, "circuit-board": CircuitBoard, settings: SettingsIcon,
  camera: Camera, brain: Brain, network: Network, radio: Radio, globe: Globe,
};

export default function Systems() {
  const [connectors, setConnectors] = useState([]);

  useEffect(() => {
    api.get("/systems").then(({ data }) => setConnectors(data.connectors)).catch(() => {});
  }, []);

  const active = connectors.filter((c) => c.status === "CONNECTED");
  const future = connectors.filter((c) => c.status !== "CONNECTED");

  return (
    <div className="space-y-6 ngx-fade-in">
      <div className="flex items-center justify-between">
        <SectionTitle sub="One NGX platform. Multiple systems. Human intelligence in the loop.">Connected Systems</SectionTitle>
        <LearnLink to="/academy/lesson/connectors" label="Learn: Connectors" testid="learn-connectors" />
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Active Connections</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {active.map((c) => {
            const Icon = ICONS[c.icon] || ServerCog;
            return (
              <Card key={c.name} className="p-5 border-emerald-900/50" data-testid={`connector-${c.type}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-md bg-emerald-950/50 border border-emerald-800 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-emerald-400" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-slate-100">{c.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{c.connector}</div>
                    </div>
                  </div>
                  <span className="flex items-center gap-2 px-2.5 py-1 rounded-sm bg-emerald-950/50 border border-emerald-800 text-emerald-300 text-[11px] font-semibold uppercase tracking-wider">
                    <StatusDot tone="success" /> Connected
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Available Architecture · Not Connected</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {future.map((c) => {
            const Icon = ICONS[c.icon] || ServerCog;
            return (
              <Card key={c.name} className="p-4 opacity-80 hover:opacity-100 transition-opacity" data-testid={`connector-${c.type}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-md bg-slate-800/60 border border-slate-700 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-medium text-slate-200 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{c.connector}</div>
                  </div>
                </div>
                <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-sm bg-slate-800/50 border border-slate-700 text-slate-400 text-[10px] font-semibold uppercase tracking-wider">
                  <StatusDot tone="idle" pulse={false} /> Not Connected
                </span>
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-4">
          These connectors represent the target architecture. They are not implemented in this MVP and are shown to
          illustrate how NGX could interface with real robots, PLCs, machines and vision systems.
        </p>
      </div>
    </div>
  );
}
